import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHeader, Loader, Badge, Empty } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError, fileUrl } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { fmtPKR, fmtDate, statusColor } from "@/lib/format";

export default function AdminDeposits() {
  const { t } = useI18n();
  const [list, setList] = useState(null);
  const [sel, setSel] = useState(null);
  const [reason, setReason] = useState("");
  const load = () => api.get("/admin/deposits").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!list) return <Loader />;

  const review = async (status) => {
    try {
      await api.put(`/admin/deposits/${sel.id}`, { status, reason });
      toast.success(`Deposit ${status}`); setSel(null); setReason(""); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const pending = list.filter((d) => d.status === "pending");

  return (
    <div>
      <PageHeader title={t("deposit_mgmt")} subtitle={`${pending.length} pending · ${list.length} total`} />
      {list.length === 0 ? <Empty label="No deposit requests." /> : (
        <div className="ec-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-gray-500 border-b border-white/10">
              {["Member", "Method", t("txn_id"), t("amount"), t("status"), "Date", ""].map((h) => <th key={h} className="text-start font-semibold px-4 py-3">{h}</th>)}
            </tr></thead>
            <tbody>
              {list.map((d) => (
                <tr key={d.id} className="border-b border-white/5" data-testid={`admin-deposit-${d.id}`}>
                  <td className="px-4 py-3 text-white">{d.user_name}</td>
                  <td className="px-4 py-3 text-gray-400">{d.method}</td>
                  <td className="px-4 py-3 text-gray-400 keep-ltr">{d.transaction_id}</td>
                  <td className="px-4 py-3 text-gray-200 font-semibold">{fmtPKR(d.amount)}</td>
                  <td className="px-4 py-3"><Badge className={statusColor(d.status)}>{d.status}</Badge></td>
                  <td className="px-4 py-3 text-gray-500">{fmtDate(d.created_at)}</td>
                  <td className="px-4 py-3"><Button size="sm" onClick={() => { setSel(d); setReason(d.reason || ""); }} data-testid={`review-deposit-${d.id}`} className="bg-orange-600 hover:bg-orange-500 font-semibold">Review</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="ec-card border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{sel?.user_name} — {t("deposit")}</DialogTitle></DialogHeader>
          {sel && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <p><span className="text-gray-500">Amount:</span> <span className="text-white font-semibold">{fmtPKR(sel.amount)}</span></p>
                <p><span className="text-gray-500">Method:</span> <span className="text-white">{sel.method}</span></p>
                <p className="col-span-2"><span className="text-gray-500">{t("txn_id")}:</span> <span className="text-white keep-ltr">{sel.transaction_id}</span></p>
              </div>
              {sel.slip && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Payment Slip</p>
                  <a href={fileUrl(sel.slip)} target="_blank" rel="noreferrer" data-testid="deposit-slip-view">
                    <img src={fileUrl(sel.slip)} alt="slip" className="rounded-lg border border-white/10 w-full max-h-72 object-contain bg-black/40 hover:opacity-80" />
                  </a>
                </div>
              )}
              <Input placeholder="Reason (for rejection)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="deposit-reason-input" />
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={() => review("approved")} disabled={sel.status === "approved"} data-testid="deposit-approve" className="bg-emerald-600 hover:bg-emerald-500 font-semibold">{t("approve")}</Button>
                <Button onClick={() => review("rejected")} data-testid="deposit-reject" className="bg-red-600 hover:bg-red-500 font-semibold">{t("reject")}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
